/* eslint-disable camelcase */
import { type ComponentProps, type FC, lazy, Suspense, useMemo } from "react";
import { type SecondaryContentPostProps } from "@userFrontend/features/publication/types/Cassettes";
import { retryLazyLoad } from "@root/shared/utils/retryLazyLoad";
import { useWorkWithShopWorkCassettes } from "@userFrontend/features/publication/components/WorkWithShopWorkCassettes/hooks/useWorkWithShopWorkCassettes";
import { useHelloWorkCassettes } from "@userFrontend/features/publication/components/WorkWithShopWorkCassettes/hooks/useHelloWorkCassettes";
import { useAnyCareerWorkCassettes } from "@userFrontend/features/publication/components/WorkWithShopWorkCassettes/hooks/useAnyCareerWorkCassettes";
import { type PremiumBanner } from "./uiParts/PremiumBanner";
import { type PrimaryContentPostProps } from "./type";
import type { Presentation as PresentationType } from "./Presentation";

const Presentation = lazy(() =>
  retryLazyLoad<ComponentProps<typeof PresentationType>>({
    fn: () => import("./Presentation").then((module) => ({ default: module.Presentation })),
  }),
);

type WorkCassettesProps = {
  primaryContentAsync: PrimaryContentPostProps;
  secondaryContentAsync: SecondaryContentPostProps;
  login: boolean;
};

type HelloWorkCassettesProps = {
  helloWorkCassettesAsync?: {
    url: string;
    params: string[];
  };
};

type AnyCareerWorkCassettesProps = {
  anyCareerCassettesAsync?: {
    url: string;
    params: string[];
  };
};

type Props = WorkCassettesProps &
  HelloWorkCassettesProps &
  AnyCareerWorkCassettesProps & {
    premiumBanner?: ComponentProps<typeof PremiumBanner>;
  };

export const WorkWithShopWorkCassettes: FC<Props> = ({
  primaryContentAsync,
  helloWorkCassettesAsync,
  anyCareerCassettesAsync,
  secondaryContentAsync,
  login,
  premiumBanner,
}) => {
  const helloWorkCassettesUrl = helloWorkCassettesAsync?.url ?? "";
  const helloWorkCassettesParams = useMemo(
    () => helloWorkCassettesAsync?.params ?? [],
    [helloWorkCassettesAsync],
  );

  const anyCareerWorkCassettesUrl = anyCareerCassettesAsync?.url ?? "";
  const anyCareerWorkCassettesParams = useMemo(
    () => anyCareerCassettesAsync?.params ?? [],
    [anyCareerCassettesAsync],
  );

  const { workWithShopWorks } = useWorkWithShopWorkCassettes({
    url: primaryContentAsync.url,
    params: primaryContentAsync.params,
    isLogin: login,
    secondaryContentAsync,
  });

  const { helloWorkRawWorks } = useHelloWorkCassettes({
    url: helloWorkCassettesUrl,
    params: helloWorkCassettesParams,
  });

  const { anyCareerWorkRawWorks } = useAnyCareerWorkCassettes({
    url: anyCareerWorkCassettesUrl,
    params: anyCareerWorkCassettesParams,
  });

  if (!workWithShopWorks.length && !helloWorkRawWorks.length && !anyCareerWorkRawWorks.length)
    return null;

  return (
    <Suspense fallback={null}>
      <Presentation
        workWithShopWorks={workWithShopWorks}
        helloWorkRawWorks={helloWorkRawWorks}
        anyCareerWorkRawWorks={anyCareerWorkRawWorks}
        premiumBanner={premiumBanner}
      />
    </Suspense>
  );
};
